import { serviceVariationLabel } from "@/services/backend/omni/servicevariation/types/service-variation";
import t from "@/lang/lang";
import { EditorVariable } from "@/shared/components/editor/editor-variables";
import { Offer } from "@/services/backend/omni/offer/types/offer";
import { ScopeOfWork } from "@/services/backend/omni/scope-of-work/types/scope-of-work";
import { serviceKindTextVariables } from "@/services/backend/omni/servicekindrules/text-variables";

const positionsVariable = "$Positionen";

/** Mirrors `scopeOfWorkVariables`, backend/core/omni/adapters/driving/gotenbergpdf/scope_of_work_variables.go. */
export function scopeOfWorkVariableNames(
  scopeOfWorks: Pick<ScopeOfWork, "title" | "serviceVariation">[],
): string[] {
  const result: string[] = [];
  scopeOfWorks.forEach((sow, i) => {
    const words =
      `${sow.title} ${serviceVariationLabel(sow.serviceVariation)}`.match(
        /[\p{L}\p{N}]+/gu,
      ) ?? [String(i + 1)];
    const name = `${positionsVariable}_${words.join("_")}`;

    let unique = name;
    for (let n = 2; result.includes(unique); n++) {
      unique = `${name}_${n}`;
    }
    result.push(unique);
  });
  return result;
}

const positionsTextVariable: EditorVariable = {
  name: positionsVariable,
  label: t("Positionen"),
  description: t("Positionen aller Leistungsverzeichnisse"),
};

const pricingTextVariables: EditorVariable[] = [
  {
    name: "$Preise",
    label: t("Preise"),
    description: t("Tabelle der Preise"),
  },
  {
    name: "$Bereichsaufteilung",
    label: t("Bereichsaufteilung"),
    description: t("Tabelle der Preise je Bereich"),
  },
];

/**
 * Mirrors the variables of `newOfferData` and `newContractData`,
 * backend/core/omni/adapters/driving/gotenbergpdf/offer.go and
 * contract.go: the text of an offer and of a contract take the same,
 * plus those of their service kind.
 */
export function offerTextVariables(
  document: Pick<Offer, "scopeOfWork" | "serviceKind">,
): EditorVariable[] {
  const names = scopeOfWorkVariableNames(document.scopeOfWork);

  return [
    positionsTextVariable,
    ...document.scopeOfWork.map((sow: ScopeOfWork, i: number) => ({
      name: names[i],
      label:
        sow.title ||
        serviceVariationLabel(sow.serviceVariation) ||
        `${t("Leistungsverzeichnis")} ${i + 1}`,
      description: t("Positionen des Leistungsverzeichnisses"),
    })),
    ...pricingTextVariables,
    ...serviceKindTextVariables(document.serviceKind),
  ];
}

/**
 * baseDataTextVariables returns the variables a text of the offer and
 * contract base data of the service kind may use, see
 * {@link offerTextVariables}. Base data has no scope of works, so the
 * variables of single scope of works are left out.
 */
export function baseDataTextVariables(serviceKind: string): EditorVariable[] {
  return [
    positionsTextVariable,
    ...pricingTextVariables,
    ...serviceKindTextVariables(serviceKind),
  ];
}
